import React, { useState } from 'react';
import { addHours, isAfter, isBefore, addDays } from 'date-fns';
import { safeFormat, safeFormatInTimeZone, validateAndFormatDate } from '@/utils/date-helpers';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import DateTimeSelector from '@/components/booking/DateTimeSelector';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getUserTimezone } from '@/utils/timezone-helpers';
import type { Appointment } from '@/components/dashboard/client/types/appointmentTypes';

interface TimeChangeRequestModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
  onRequestSubmitted: () => void;
}

export function TimeChangeRequestModal({
  appointment,
  isOpen,
  onClose,
  onRequestSubmitted
}: TimeChangeRequestModalProps) {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userTimezone, setUserTimezone] = useState<string>('UTC');

  // Safely parse appointment dates with validation
  const appointmentDate = React.useMemo(() => {
    return validateAndFormatDate(appointment.start_time) || new Date();
  }, [appointment.start_time]);
  
  const appointmentEndDate = React.useMemo(() => {
    return validateAndFormatDate(appointment.end_time) || new Date();
  }, [appointment.end_time]);
  const now = new Date();
  const twentyFourHoursFromNow = addHours(now, 24);

  // Check if appointment can be modified (24 hours before start time)
  const canModify = isAfter(appointmentDate, twentyFourHoursFromNow);

  // Fetch user timezone
  React.useEffect(() => {
    const fetchTimezone = async () => {
      if (user?.id) {
        try {
          const timezone = await getUserTimezone(user.id);
          setUserTimezone(timezone);
        } catch (error) {
          console.error('Error fetching user timezone:', error);
        }
      }
    };
    fetchTimezone();
  }, [user?.id]);

  // Format date with user timezone and language - with error handling
  const formatDateForUser = (date: Date | null): string => {
    if (!date) return 'Invalid Date';
    
    // Validate the date before formatting
    const validDate = validateAndFormatDate(date);
    if (!validDate) return 'Invalid Date';
    
    const language = t('common:language') === 'zh' ? 'zh' : 'en';
    if (language === 'zh') {
      return safeFormatInTimeZone(validDate, userTimezone, 'yyyy年MM月dd日 EEEE');
    } else {
      return safeFormatInTimeZone(validDate, userTimezone, 'MMMM dd, yyyy EEEE');
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || !reason.trim()) {
      toast({
        title: t('timeChangeRequests.modal.errorTitle'),
        description: t('timeChangeRequests.modal.fillAllFields'),
        variant: "destructive",
      });
      return;
    }

    if (!canModify) {
      toast({
        title: t('timeChangeRequests.modal.errorTitle'), 
        description: t('timeChangeRequests.modal.cannotModifyTime'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse the selected time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const requestedStartTime = new Date(selectedDate);
      requestedStartTime.setHours(hours, minutes, 0, 0);

      // Calculate end time based on original appointment duration
      const originalDuration = appointmentEndDate.getTime() - appointmentDate.getTime();
      const requestedEndTime = new Date(requestedStartTime.getTime() + originalDuration);

      // Validate that the requested time is in the future
      if (isBefore(requestedStartTime, now)) {
        toast({
          title: t('timeChangeRequests.modal.errorTitle'),
          description: t('timeChangeRequests.modal.cannotSelectPastTime'),
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Validate that the requested time is at least 24 hours from now
      if (isBefore(requestedStartTime, twentyFourHoursFromNow)) {
        toast({
          title: t('timeChangeRequests.modal.errorTitle'),
          description: t('timeChangeRequests.modal.newTimeMustBe24Hours'),
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Submit the time change request
      const { error } = await supabase
        .from('booking_time_change_requests')
        .insert({
          booking_id: appointment.id,
          client_id: user?.id || '', // Use authenticated user's ID
          practitioner_id: appointment.practitioner_id,
          original_start_time: appointment.start_time,
          original_end_time: appointment.end_time,
          requested_start_time: requestedStartTime.toISOString(),
          requested_end_time: requestedEndTime.toISOString(),
          client_reason: reason.trim(),
        });

      if (error) {
        console.error('Error submitting time change request:', error);
        toast({
          title: t('timeChangeRequests.modal.submitFailed'),
          description: t('timeChangeRequests.modal.submitError'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t('timeChangeRequests.modal.requestSubmitted'),
        description: t('timeChangeRequests.modal.requestSubmittedDesc'),
      });

      onRequestSubmitted();
      onClose();
    } catch (error) {
      console.error('Error submitting time change request:', error);
      toast({
        title: t('timeChangeRequests.modal.submitFailed'),
        description: t('timeChangeRequests.modal.networkError'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setSelectedTime('');
    setReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('timeChangeRequests.modal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current appointment info */}
          <div className="border rounded-lg p-4 bg-muted/50">
            <h3 className="font-semibold mb-2">{t('timeChangeRequests.modal.currentInfo')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDateForUser(appointmentDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{safeFormat(appointmentDate, 'HH:mm')} - {safeFormat(appointmentEndDate, 'HH:mm')}</span>
              </div>
              <div>
                <span className="font-medium">{t('timeChangeRequests.modal.service')}</span>
                <span>{appointment.service?.service_name}</span>
              </div>
              <div>
                <span className="font-medium">{t('timeChangeRequests.modal.practitioner')}</span>
                <span>{appointment.practitioner?.first_name} {appointment.practitioner?.last_name}</span>
              </div>
            </div>
          </div>

          {/* Time modification restrictions */}
          {!canModify && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('timeChangeRequests.modal.cannotModify')}
              </AlertDescription>
            </Alert>
          )}

          {canModify && (
            <>
              {/* Date and time selector */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">{t('timeChangeRequests.modal.selectNewTime')}</Label>
                <DateTimeSelector
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  selectedTime={selectedTime}
                  onTimeChange={setSelectedTime}
                  practitionerId={appointment.practitioner_id}
                  duration={60} // Default duration
                />
              </div>

              {/* Reason input */}
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-base font-semibold">
                  {t('timeChangeRequests.modal.reasonLabel')} <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('timeChangeRequests.modal.reasonPlaceholder')}
                  className="min-h-[100px]"
                />
              </div>

              {/* Preview of new time */}
              {selectedDate && selectedTime && (
                <div className="border rounded-lg p-4 bg-primary/5">
                  <h3 className="font-semibold mb-2">{t('timeChangeRequests.modal.newTimePreview')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDateForUser(selectedDate)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{selectedTime} - {safeFormat(new Date(selectedDate.getTime() + (appointmentEndDate.getTime() - appointmentDate.getTime())), 'HH:mm')}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('timeChangeRequests.modal.cancel')}
            </Button>
            {canModify && (
              <Button
                onClick={handleSubmit}
                disabled={!selectedDate || !selectedTime || !reason.trim() || isSubmitting}
              >
                {isSubmitting ? t('timeChangeRequests.modal.submitting') : t('timeChangeRequests.modal.submit')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}